import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';

export interface ReferralCodeRow {
  code: string;
  patient_id: string;
  customer_name: string | null;
  customer_email: string | null;
  referrals_sent: number;
  orders_placed: number;
  orders_shipped: number;
  credit_earned_cents: number;
  created_at: string | null;
}

export interface ReferralCodesPage {
  codes: ReferralCodeRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReferralSummary {
  codes_total: number;
  codes_active: number;
  referrals_sent: number;
  referrals_30d: number;
  referred_customers: number;
  referred_with_order: number;
  orders_placed: number;
  orders_shipped: number;
  credit_earned_cents: number;
}

export interface ReferredCustomer {
  patient_id: string;
  name: string | null;
  email: string | null;
  orders_placed: number;
  orders_shipped: number;
  attributed_at: string | null;
}

export interface ReferralCodeDetail {
  code: string;
  stripe_promotion_code_id: string | null;
  referrer: { patient_id: string; name: string | null; email: string | null };
  referrals_sent: number;
  orders_placed: number;
  orders_shipped: number;
  credit_earned_cents: number;
  created_at: string | null;
  referred_customers: ReferredCustomer[];
}

export interface CreateReferralCodeResponse {
  created: boolean;
  code: string | null;
  patient_id: string | null;
  stripe_promotion_code_id: string | null;
  reason: string | null;
}

/** Program-level KPI header (one aggregated SQL call server-side). */
export function useReferralSummary() {
  return useQuery({
    queryKey: ['admin', 'referrals', 'summary'],
    queryFn: () => apiFetch<ReferralSummary>('/admin/referrals/summary'),
    staleTime: 30_000,
  });
}

export interface ReferralCodesParams {
  q: string;
  activeOnly: boolean;
  page: number; // 0-based
  pageSize?: number;
}

/** Server-paginated per-code conversion table. The backend aggregates in
 *  SQL — never fetch the full code list client-side (1.5k+ codes is what
 *  broke the original all-rows implementation). */
export function useReferralCodes({
  q,
  activeOnly,
  page,
  pageSize = 25,
}: ReferralCodesParams) {
  const params = new URLSearchParams({
    active_only: String(activeOnly),
    limit: String(pageSize),
    offset: String(page * pageSize),
  });
  if (q.trim()) params.set('q', q.trim());
  return useQuery({
    queryKey: ['admin', 'referrals', 'codes', { q: q.trim(), activeOnly, page, pageSize }],
    queryFn: () => apiFetch<ReferralCodesPage>(`/admin/referrals/codes?${params}`),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useReferralCodeDetail(code: string | null) {
  return useQuery({
    queryKey: ['admin', 'referrals', 'codes', code],
    queryFn: () =>
      apiFetch<ReferralCodeDetail>(
        `/admin/referrals/codes/${encodeURIComponent(code as string)}`,
      ),
    enabled: Boolean(code),
    staleTime: 30_000,
  });
}

export function useCreateReferralCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<CreateReferralCodeResponse>('/admin/referrals/codes', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// ---- one-shot account-create-and-mint-code (for influencers / partners) ----

export interface CreateAccountRequest {
  email: string;
  first_name: string;
  last_name: string;
}

export interface CreateAccountResponse {
  patient_id: string;
  email: string;
  code: string;
  referral_link: string;
  already_existed: boolean;
}

/** Creates a passwordless portal account (Supabase user + Healthie patient
 *  + patients row) AND mints a referral code in one call. Idempotent on
 *  email — re-running returns the existing patient + their existing code
 *  with `already_existed=true`. */
export function useCreateAccountWithReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateAccountRequest) =>
      apiFetch<CreateAccountResponse>('/admin/referrals/accounts', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

export interface DeleteReferralCodeResponse {
  deleted: boolean;
  reason?: string;
  redemptions?: number;
  code?: string;
}

export function useDeleteReferralCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<DeleteReferralCodeResponse>(
        `/admin/referrals/codes/${encodeURIComponent(code)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
  });
}

// ---- patient typeahead (reuses the clinical-admin patient search) ----

export interface PatientSearchResult {
  id: string;
  email: string | null;
  display_name: string | null;
}

interface PatientSearchResponse {
  results: PatientSearchResult[];
}

/** Search the `patients` table by name/email. Disabled until the query is
 *  at least 2 chars so we don't fire on every keystroke. */
export function usePatientSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['admin', 'patient-search', q],
    queryFn: () =>
      apiFetch<PatientSearchResponse>(
        `/admin/clinical-admin/patients/search?q=${encodeURIComponent(q)}&limit=8`,
      ),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}
